/**
 * Ticket LM1: Detailseite eines Freebies.
 *
 * Anlegen, pflegen, Datei hinterlegen, veröffentlichen. Werkzeug-Freebies
 * schalten nach der Bestätigung ein Werkzeug im Cockpit frei (Ticket LM3).
 *
 * Ticket LM7: Deaktivieren bleibt der Regelfall. Gelöscht werden darf ein
 * Lead-Magnet nur, solange niemand ihn genutzt hat – keine Anmeldungen, kein
 * Kunde und kein Kontakt mit diesem Herkunftsvermerk. Geprüft wird das
 * serverseitig, gelöscht ausschließlich hier auf der Detailseite und nur von
 * Administratoren.
 */


import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { AdminUploadButton } from "@/components/admin/AdminUploadButton";

import { supabase } from "@/integrations/supabase/client";
import { sanitizeStorageFilename } from "@/lib/storage-filename";
import { confirmDialog } from "@/lib/confirm-dialog";
import { DELETE_VS_DEACTIVATE_HINT } from "@/lib/lead-magnet-delete";
import {
  deleteLeadMagnet,
  previewLeadMagnetDeletion,
} from "@/lib/lead-magnet-delete.functions";
import { useAdminSession } from "@/hooks/use-admin-session";

import { MarkdownEditor } from "@/components/MarkdownEditor.lazy";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";

import { AdminCard } from "@/components/admin/AdminCard";
import { OfferPeopleManager } from "@/components/admin/OfferPeopleManager";
import { AdminErrorNotice } from "@/components/admin/AdminErrorNotice";
import {
  LEAD_MAGNET_KIND_LABELS,
  LEAD_MAGNET_OVERVIEW_PATH,
  LEAD_MAGNET_PREVIEW_BUCKET,
  LEAD_MAGNET_MAX_QUESTIONS,
  LEAD_MAGNET_STATUS_LABELS,
  DEFAULT_THANKS_SLUG,
  deriveLeadMagnetSlug,
  describeConsentVersion,
  isLeadMagnetSlugLocked,
  leadMagnetFormSchema,
  leadMagnetPath,
  leadMagnetSlugIssue,
  leadMagnetThanksPath,
  type LeadMagnetFormValues,
  type LeadMagnetKind,
  type LeadMagnetQuestion,
  type LeadMagnetStatus,
} from "@/lib/lead-magnets";
import { CLIENT_MODULE_LABELS, type ClientModuleKey } from "@/lib/client-modules";
import { applyLeadMagnetSlugRedirect } from "@/lib/lead-magnet-redirects.functions";

export const Route = createFileRoute("/_admin/admin/lead-magnets/$id")({
  head: () => ({
    meta: [
      { title: "Lead-Magnet – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LeadMagnetDetailPage,
});

const BUCKET = "lead-magnet-files";

const EMPTY: LeadMagnetFormValues = {
  slug: "",
  title: "",
  subtitle: "",
  shortDescription: "",
  bodyMd: "",
  seoTitle: "",
  seoDescription: "",

  kind: "datei",
  questions: [],
  consentText:
    "Ich möchte diese Unterlage erhalten und in Zukunft passende Hinweise von creategy per E-Mail bekommen. Diese Einwilligung kann ich jederzeit widerrufen.",
  confirmationText: "",
  status: "entwurf",
  filePath: null,
  fileName: null,
  fileSize: null,
  previewImagePath: null,
  accessModuleKey: null,
};

const inputClass =
  "mt-2 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm";
const labelClass = "block text-sm font-medium";

function LeadMagnetDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { role } = useAdminSession();
  const isNew = id === "neu";

  const [values, setValues] = useState<LeadMagnetFormValues>(EMPTY);
  /**
   * Ticket LM6: Solange niemand das Kürzel selbst angefasst hat und das Freebie
   * nie veröffentlicht war, folgt es dem Titel. Danach bleibt es stehen.
   */
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugUnlocked, setSlugUnlocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  /** Ticket WS1: Hochladen des Vorschaubilds für die Übersichtsseite. */
  const [previewUploading, setPreviewUploading] = useState(false);
  /** Ticket LM7: Löschen nur mit eingetragenem Titel. */
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);


  const record = useQuery({
    queryKey: ["admin-lead-magnet", id],
    enabled: !isNew,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lead_magnets")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const signups = useQuery({
    queryKey: ["admin-lead-magnet-signups", id],
    enabled: !isNew,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lead_magnet_signups")
        .select("id, email, first_name, last_name, answers, confirmed_at, created_at")
        .eq("lead_magnet_id", id)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });

  useEffect(() => {
    const row = record.data as Record<string, any> | null | undefined;
    if (!row) return;
    setValues({
      slug: row.slug,
      title: row.title,
      subtitle: row.subtitle ?? "",
      shortDescription: row.short_description ?? "",
      bodyMd: row.body_md ?? "",
      seoTitle: row.seo_title ?? "",
      seoDescription: row.seo_description ?? "",

      kind: row.kind as LeadMagnetKind,
      questions: (row.questions as LeadMagnetQuestion[]) ?? [],
      consentText: row.consent_text ?? "",
      confirmationText: row.confirmation_text ?? "",
      status: row.status as LeadMagnetStatus,
      filePath: row.file_path ?? null,
      fileName: row.file_name ?? null,
      fileSize: row.file_size ?? null,
      previewImagePath: row.preview_image_path ?? null,
      accessModuleKey: (row.access_module_key as ClientModuleKey | null) ?? null,
    });
    setSlugTouched(true);
  }, [record.data]);

  /** Ticket LM6: bereits belegte Kürzel für die Prüfung direkt im Formular. */
  const otherSlugs = useQuery({
    queryKey: ["admin-lead-magnet-slugs", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("lead_magnets").select("id, slug");
      if (error) throw new Error(error.message);
      return ((data ?? []) as { id: string; slug: string }[])
        .filter((row) => row.id !== id)
        .map((row) => row.slug);
    },
  });

  const row = record.data as Record<string, any> | null | undefined;
  const firstPublishedAt = (row?.first_published_at as string | null) ?? null;
  const consentVersionNo = (row?.consent_version_no as number | undefined) ?? 1;
  const consentVersionChangedAt = (row?.consent_version_changed_at as string | null) ?? null;
  const slugLocked = isLeadMagnetSlugLocked(firstPublishedAt, slugUnlocked);
  /** Ticket LM11: Umleitung von der alten auf die neue Adresse. */
  const setSlugRedirect = useServerFn(applyLeadMagnetSlugRedirect);

  // Ticket LM6: Kürzel entsteht aus dem Titel, bis jemand es selbst anfasst.
  useEffect(() => {
    if (slugTouched || !isNew) return;
    const derived = deriveLeadMagnetSlug(values.title);
    setValues((prev) => (prev.slug === derived ? prev : { ...prev, slug: derived }));
  }, [values.title, slugTouched, isNew]);

  const slugIssue = useMemo(
    () => leadMagnetSlugIssue(values.slug, otherSlugs.data ?? []),
    [values.slug, otherSlugs.data],
  );

  const confirmedCount = useMemo(
    () => (signups.data ?? []).filter((s: Record<string, any>) => s.confirmed_at).length,
    [signups.data],
  );

  // Ticket LM7: Nutzungsprüfung serverseitig – nur für Administratoren sichtbar.
  const previewDeletion = useServerFn(previewLeadMagnetDeletion);
  const runDeletion = useServerFn(deleteLeadMagnet);
  const deletion = useQuery({
    queryKey: ["admin-lead-magnet-deletion", id],
    enabled: !isNew && role === "admin",
    queryFn: () => previewDeletion({ data: { id } }),
  });

  async function onDelete() {
    const info = deletion.data;
    if (!info) return;
    const ok = await confirmDialog({
      title: `„${info.title}" löschen?`,
      description: `Der Lead-Magnet „${info.title}" wird vollständig entfernt, samt hinterlegter Datei. Das lässt sich nicht zurücknehmen. ${DELETE_VS_DEACTIVATE_HINT}`,
      confirmLabel: "Endgültig löschen",
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await runDeletion({ data: { id, confirmation: deleteConfirm } });
      toast.success(`„${info.title}" gelöscht.`);
      navigate({ to: "/admin/lead-magnets" });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Löschen fehlgeschlagen.");
      await deletion.refetch();
    } finally {
      setDeleting(false);
    }
  }

  function patch(next: Partial<LeadMagnetFormValues>) {
    setValues((prev) => ({ ...prev, ...next }));
  }


  async function onUpload(file: File) {
    setUploading(true);
    try {
      const path = `${crypto.randomUUID()}/${sanitizeStorageFilename(file.name)}`;
      const { error } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { upsert: false, contentType: file.type });
      if (error) throw new Error(error.message);
      const previous = values.filePath;
      patch({ filePath: path, fileName: file.name, fileSize: file.size });
      if (previous) await supabase.storage.from(BUCKET).remove([previous]);
      toast.success("Datei hinterlegt. Bitte noch speichern.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  }

  /**
   * Ticket WS1: Vorschaubild in den eigenen Ablagebereich legen. Das alte Objekt
   * verschwindet mit, damit keine Waisen zurückbleiben.
   */
  async function onPreviewUpload(file: File) {
    setPreviewUploading(true);
    try {
      const path = `${crypto.randomUUID()}/${sanitizeStorageFilename(file.name)}`;
      const { error } = await supabase.storage
        .from(LEAD_MAGNET_PREVIEW_BUCKET)
        .upload(path, file, { upsert: false, contentType: file.type });
      if (error) throw new Error(error.message);
      const previous = values.previewImagePath;
      patch({ previewImagePath: path });
      if (previous) {
        await supabase.storage.from(LEAD_MAGNET_PREVIEW_BUCKET).remove([previous]);
      }
      toast.success("Vorschaubild hinterlegt. Bitte noch speichern.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Upload fehlgeschlagen.");
    } finally {
      setPreviewUploading(false);
    }
  }

  async function onPreviewRemove() {
    const previous = values.previewImagePath;
    if (!previous) return;
    patch({ previewImagePath: null });
    await supabase.storage.from(LEAD_MAGNET_PREVIEW_BUCKET).remove([previous]);
    toast.success("Vorschaubild entfernt. Bitte noch speichern.");
  }

  async function onSave() {
    const parsed = leadMagnetFormSchema.safeParse(values);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Bitte Eingaben prüfen.");
      return;
    }
    if (slugIssue) {
      toast.error(slugIssue);
      return;
    }
    const v = parsed.data;
    setSaving(true);
    try {
      const payload = {
        slug: v.slug,
        thanks_slug: DEFAULT_THANKS_SLUG,
        title: v.title,
        subtitle: v.subtitle,
        short_description: v.shortDescription,
        body_md: v.bodyMd,
        seo_title: v.seoTitle,
        seo_description: v.seoDescription,

        kind: v.kind,
        questions: v.questions,
        consent_text: v.consentText,
        confirmation_text: v.confirmationText,
        status: v.status,
        file_path: v.filePath,
        file_name: v.fileName,
        file_size: v.fileSize,
        preview_image_path: v.previewImagePath,
        access_module_key: v.accessModuleKey,
      };
      if (isNew) {
        const { data, error } = await supabase
          .from("lead_magnets")
          .insert(payload)
          .select("id")
          .single();
        if (error) throw new Error(error.message);
        toast.success("Lead-Magnet angelegt.");
        navigate({ to: "/admin/lead-magnets/$id", params: { id: (data as { id: string }).id } });
      } else {
        const previousSlug = ((record.data as Record<string, any> | null)?.slug as string) ?? "";
        const { error } = await supabase.from("lead_magnets").update(payload).eq("id", id);
        if (error) throw new Error(error.message);
        toast.success("Gespeichert.");
        // Ticket LM11: War das Freebie schon veröffentlicht und ändert sich das
        // Kürzel, entsteht die Umleitung von selbst – in der vorhandenen
        // Weiterleitungsverwaltung, ohne zweite Liste.
        if (previousSlug && previousSlug !== v.slug && firstPublishedAt) {
          try {
            await setSlugRedirect({ data: { oldSlug: previousSlug, newSlug: v.slug } });
            toast.success(
              `Umleitung angelegt: ${leadMagnetPath(previousSlug)} führt jetzt auf ${leadMagnetPath(v.slug)}.`,
            );
          } catch (redirectError) {
            toast.error(
              redirectError instanceof Error
                ? `Umleitung nicht angelegt: ${redirectError.message}`
                : "Umleitung nicht angelegt.",
            );
          }
        }
        await record.refetch();
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  function addQuestion() {
    if (values.questions.length >= LEAD_MAGNET_MAX_QUESTIONS) return;
    patch({
      questions: [
        ...values.questions,
        { id: `f${values.questions.length + 1}`, label: "", type: "text", options: [], required: false },
      ],
    });
  }

  function patchQuestion(index: number, next: Partial<LeadMagnetQuestion>) {
    patch({
      questions: values.questions.map((q, i) => (i === index ? { ...q, ...next } : q)),
    });
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title={isNew ? "Neuer Lead-Magnet" : values.title || "Lead-Magnet"}
        description={
          <>
            Landingpage: <code>{leadMagnetPath(values.slug || "kuerzel")}</code> · Dankeseite:{" "}
            <code>{leadMagnetThanksPath(values.slug || "kuerzel", DEFAULT_THANKS_SLUG)}</code>
          </>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/admin/lead-magnets"
              className="rounded-sm border border-border px-3 py-2 text-sm"
            >
              Zur Liste
            </Link>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-sm bg-brand-button px-4 py-2 font-display text-sm font-extrabold text-brand-button-foreground disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Speichern
            </button>
          </div>
        }
      />

      {record.error && <AdminErrorNotice message={(record.error as Error).message} />}

      <AdminCard
        title="Das sieht der Besucher auf der Landingpage"
        description="Titel, Untertitel und Kurzbeschreibung. Der ausführliche Text steht weiter unten in eigenem Abschnitt."
      >
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass} htmlFor="lm-title">
              Titel
            </label>
            <input
              id="lm-title"
              className={inputClass}
              value={values.title}
              onChange={(e) => patch({ title: e.target.value })}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="lm-slug">
              Kürzel der Landingpage
            </label>
            <input
              id="lm-slug"
              className={inputClass}
              value={values.slug}
              disabled={slugLocked}
              onChange={(e) => {
                setSlugTouched(true);
                patch({ slug: e.target.value });
              }}
            />
            <p className="mt-2 font-body text-xs text-muted-foreground">
              Entsteht von selbst aus dem Titel. Adresse:{" "}
              <code>{leadMagnetPath(values.slug || "kuerzel")}</code>
            </p>
            {slugLocked && (
              <div className="mt-2 space-y-1">
                <p className="font-body text-xs text-muted-foreground">
                  Seit der ersten Veröffentlichung bleibt das Kürzel stehen, auch wenn der Titel
                  sich ändert. Bei einer Änderung entsteht automatisch eine Umleitung von der
                  alten Adresse.
                </p>
                <button
                  type="button"
                  className="font-body text-xs text-destructive underline"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Das Kürzel steht in Anzeigen und Verlinkungen. Beim Speichern entsteht automatisch eine Umleitung von der alten auf die neue Adresse, verteilte Links kommen also weiter an. Trotzdem: gedruckte Adressen und Auswertungen ändern sich. Wirklich ändern?",
                      )
                    ) {
                      setSlugUnlocked(true);
                      setSlugTouched(true);
                    }
                  }}
                >
                  Kürzel trotzdem ändern
                </button>
              </div>
            )}
            {slugIssue && (
              <p className="mt-2 font-body text-xs text-destructive">{slugIssue}</p>
            )}
          </div>
          <div>
            <label className={labelClass} htmlFor="lm-kind">
              Auslieferung
            </label>
            <select
              id="lm-kind"
              className={inputClass}
              value={values.kind}
              onChange={(e) => patch({ kind: e.target.value as LeadMagnetKind })}
            >
              {(Object.keys(LEAD_MAGNET_KIND_LABELS) as LeadMagnetKind[]).map((k) => (
                <option key={k} value={k}>
                  {LEAD_MAGNET_KIND_LABELS[k]}
                </option>
              ))}
            </select>
            <p className="mt-2 font-body text-xs text-muted-foreground">
              Entscheidet, was jemand nach der Bestätigung bekommt: eine Datei zum Herunterladen
              oder einen Zugang zum Cockpit.
            </p>
          </div>
          {values.kind === "werkzeug" && (
            <div>
              <label className={labelClass} htmlFor="lm-module">
                Freizuschaltendes Werkzeug
              </label>
              <select
                id="lm-module"
                className={inputClass}
                value={values.accessModuleKey ?? ""}
                onChange={(e) =>
                  patch({ accessModuleKey: (e.target.value || null) as ClientModuleKey | null })
                }
              >
                <option value="">Bitte wählen</option>
                {(Object.keys(CLIENT_MODULE_LABELS) as ClientModuleKey[]).map((k) => (
                  <option key={k} value={k}>
                    {CLIENT_MODULE_LABELS[k]}
                  </option>
                ))}
              </select>
              <p className="mt-2 font-body text-xs text-muted-foreground">
                Neue Zugänge über dieses Freebie erhalten die Kategorie „Interessent“ und den
                eingeschränkten Umfang. Bestehende Kunden behalten ihre Kategorie und bekommen
                das Werkzeug im vollen Umfang.
              </p>
            </div>
          )}
          <div className="md:col-span-2">
            <label className={labelClass} htmlFor="lm-subtitle">
              Untertitel
            </label>
            <input
              id="lm-subtitle"
              className={inputClass}
              value={values.subtitle}
              onChange={(e) => patch({ subtitle: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass} htmlFor="lm-short-description">
              Kurzbeschreibung für die Übersichtsseite
            </label>
            <textarea
              id="lm-short-description"
              rows={3}
              maxLength={200}
              className={inputClass}
              value={values.shortDescription}
              onChange={(e) => patch({ shortDescription: e.target.value })}
            />
            <p className="mt-2 font-body text-xs text-muted-foreground">
              Erscheint auf {LEAD_MAGNET_OVERVIEW_PATH} und in der internen Liste. Zwei Sätze
              genügen: {values.shortDescription.length} von 200 Zeichen.
            </p>
          </div>
        </div>
      </AdminCard>

      {/*
        Ticket LM8: Ein Textbereich statt drei. Was früher „Beschreibung“ hieß,
        steht jetzt hier am Anfang – die Kurzbeschreibung oben trägt die
        Übersicht, dieser Text trägt die Seite selbst.
      */}
      <AdminCard
        title="Ausführlicher Text auf der Landingpage"
        description="Steht unter dem Formularbereich. Hier gehört hin, was überzeugt und was Suchmaschinen lesen sollen."
      >
        <div className="mt-4">
          <MarkdownEditor
            value={values.bodyMd}
            onChange={(v) => patch({ bodyMd: v })}
            rows={20}
          />
        </div>
      </AdminCard>

      {/* Ticket W11: Personenkarten der Landingpage. Speichert sofort. */}
      <AdminCard
        title="Ansprechpersonen"
        description="Erscheinen als Karten unter dem Formularbereich. Ohne Zuordnung erscheint der Abschnitt nicht."
      >
        <div className="mt-4">
          <OfferPeopleManager offer={isNew ? null : { kind: "lead_magnet", id }} />
        </div>
      </AdminCard>

      <AdminCard
        title="Angaben für Suchmaschinen"
        description="Bleiben die Felder leer, gilt weiter die Ableitung aus Titel und Untertitel."
      >
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass} htmlFor="lm-seo-title">
              Titel für Suchmaschinen
            </label>
            <input
              id="lm-seo-title"
              className={inputClass}
              maxLength={70}
              value={values.seoTitle}
              onChange={(e) => patch({ seoTitle: e.target.value })}
            />
            <p className="mt-2 font-body text-xs text-muted-foreground">
              {values.seoTitle.length} von 70 Zeichen.
            </p>
          </div>
          <div>
            <label className={labelClass} htmlFor="lm-seo-description">
              Beschreibung für Suchmaschinen
            </label>
            <textarea
              id="lm-seo-description"
              rows={3}
              maxLength={160}
              className={inputClass}
              value={values.seoDescription}
              onChange={(e) => patch({ seoDescription: e.target.value })}
            />
            <p className="mt-2 font-body text-xs text-muted-foreground">
              {values.seoDescription.length} von 160 Zeichen.
            </p>
          </div>
        </div>
      </AdminCard>


      <AdminCard
        title="Datei"
        description="Liegt in einem privaten Ablagebereich. Besucher bekommen nur kurzlebige Links."
      >
        <div className="mt-4">
          <AdminUploadButton
            label={values.fileName ? "Datei ersetzen" : "Datei hochladen"}
            uploading={uploading}
            currentName={values.fileName || null}
            hint={values.fileName ? undefined : "Noch keine Datei hinterlegt."}
            onFiles={(files) => {
              const file = files[0];
              if (file) void onUpload(file);
            }}
          />
        </div>
      </AdminCard>

      <AdminCard
        title="Vorschaubild für die Übersichtsseite"
        description={`Erscheint auf ${LEAD_MAGNET_OVERVIEW_PATH}, auf der Landingpage und als Vorschaubild beim Teilen des Links. Eigener Ablagebereich, getrennt von der Freebie-Datei. Fehlt das Bild, bleibt die Fläche leer und der geteilte Link zeigt kein Bild.`}
      >
        <div className="mt-4">
          <AdminUploadButton
            label={values.previewImagePath ? "Vorschaubild ersetzen" : "Vorschaubild hochladen"}
            accept="image/*"
            uploading={previewUploading}
            currentName={values.previewImagePath ? values.previewImagePath.split("/").pop() : null}
            hint={values.previewImagePath ? "JPG, PNG oder WebP." : "Noch kein Vorschaubild hinterlegt. JPG, PNG oder WebP."}
            onFiles={(files) => {
              const file = files[0];
              if (file) void onPreviewUpload(file);
            }}
          >
            {values.previewImagePath ? (
              <button
                type="button"
                onClick={() => void onPreviewRemove()}
                className="inline-flex items-center gap-2 rounded-md border border-destructive/40 px-3 py-2 text-xs font-semibold text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden /> Entfernen
              </button>
            ) : null}
          </AdminUploadButton>
        </div>
      </AdminCard>


      <AdminCard
        title="Das füllt der Besucher aus"
        description={`Höchstens ${LEAD_MAGNET_MAX_QUESTIONS} Fragen. Jede weitere Frage kostet Abschlüsse.`}
        actions={
          values.questions.length < LEAD_MAGNET_MAX_QUESTIONS ? (
            <button
              type="button"
              onClick={addQuestion}
              className="rounded-sm border border-border px-3 py-2 text-sm"
            >
              Frage hinzufügen
            </button>
          ) : null
        }
      >
        <div className="mt-4 space-y-4">
          {values.questions.length === 0 && (
            <p className="text-sm text-muted-foreground">Keine Fragen — nur Name und E-Mail.</p>
          )}
          {values.questions.map((question, index) => (
            <div key={index} className="rounded-sm border border-border p-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className={labelClass} htmlFor={`q-label-${index}`}>
                    Frage
                  </label>
                  <input
                    id={`q-label-${index}`}
                    className={inputClass}
                    value={question.label}
                    onChange={(e) => patchQuestion(index, { label: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor={`q-type-${index}`}>
                    Art
                  </label>
                  <select
                    id={`q-type-${index}`}
                    className={inputClass}
                    value={question.type}
                    onChange={(e) =>
                      patchQuestion(index, { type: e.target.value as LeadMagnetQuestion["type"] })
                    }
                  >
                    <option value="text">Freitext</option>
                    <option value="auswahl">Auswahl</option>
                  </select>
                </div>
                {question.type === "auswahl" && (
                  <div className="md:col-span-2">
                    <label className={labelClass} htmlFor={`q-options-${index}`}>
                      Antworten (eine je Zeile)
                    </label>
                    <textarea
                      id={`q-options-${index}`}
                      rows={4}
                      className={inputClass}
                      value={question.options.join("\n")}
                      onChange={(e) =>
                        patchQuestion(index, {
                          options: e.target.value
                            .split("\n")
                            .map((o) => o.trim())
                            .filter(Boolean),
                        })
                      }
                    />
                  </div>
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={question.required}
                    onChange={(e) => patchQuestion(index, { required: e.target.checked })}
                  />
                  Pflichtfeld
                </label>
                <button
                  type="button"
                  className="text-sm text-destructive underline"
                  onClick={() =>
                    patch({ questions: values.questions.filter((_, i) => i !== index) })
                  }
                >
                  Frage entfernen
                </button>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard
        title="Einwilligung und was nach der Bestätigung folgt"
        description="Der Einwilligungstext steht am Formular. Der Text auf der Dankeseite ist das, was jemand nach der Bestätigung liest."
      >
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className={labelClass} htmlFor="lm-consent">
              Einwilligungstext am Formular
            </label>
            <textarea
              id="lm-consent"
              rows={4}
              className={inputClass}
              value={values.consentText}
              onChange={(e) => patch({ consentText: e.target.value })}
            />
          </div>
          <div>
            <span className={labelClass}>Fassung des Wortlauts</span>
            <p className="mt-2 rounded-sm border border-border bg-muted/40 px-3 py-2 font-body text-sm">
              {describeConsentVersion(consentVersionNo, consentVersionChangedAt)}
            </p>
            <p className="mt-2 font-body text-xs text-muted-foreground">
              Wird vom System geführt: Ändert sich der Einwilligungstext, zählt die Fassung um eins
              hoch. Bereits erteilte Einwilligungen behalten ihre Fassung.
            </p>
          </div>
          <div>
            <label className={labelClass} htmlFor="lm-status">
              Status
            </label>
            <select
              id="lm-status"
              className={inputClass}
              value={values.status}
              onChange={(e) => patch({ status: e.target.value as LeadMagnetStatus })}
            >
              {(Object.keys(LEAD_MAGNET_STATUS_LABELS) as LeadMagnetStatus[]).map((s) => (
                <option key={s} value={s}>
                  {LEAD_MAGNET_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
            <p className="mt-2 font-body text-xs text-muted-foreground">
              Nur veröffentlichte Freebies sind unter <code>/kostenlos/</code> erreichbar und stehen
              in der Sitemap. Entwürfe nehmen keine Anmeldungen an.
            </p>
            {values.kind === "werkzeug" && (
              <p className="mt-2 text-sm text-muted-foreground">
                Werkzeug-Freebies können erst mit der Kontoanlage (Ticket LM3) veröffentlicht
                werden.
              </p>
            )}
          </div>
          <div className="md:col-span-2">
            <label className={labelClass} htmlFor="lm-confirmation">
              Text auf der Dankeseite
            </label>
            <p className="mt-1 font-body text-xs text-muted-foreground">
              Das liest jemand nach der Bestätigung unter{" "}
              <code>{leadMagnetThanksPath(values.slug || "kuerzel", DEFAULT_THANKS_SLUG)}</code>.
            </p>
            <textarea
              id="lm-confirmation"
              rows={3}
              className={inputClass}
              value={values.confirmationText}
              onChange={(e) => patch({ confirmationText: e.target.value })}
            />
          </div>
        </div>
      </AdminCard>

      {!isNew && (
        <AdminCard
          title="Anmeldungen"
          description={`${confirmedCount} bestätigt von ${(signups.data ?? []).length} Anmeldungen (jüngste 200).`}
        >
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-2 pr-4">E-Mail</th>
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Bestätigt</th>
                  <th className="py-2">Eingang</th>
                </tr>
              </thead>
              <tbody>
                {(signups.data ?? []).map((row: Record<string, any>) => (
                  <tr key={row.id} className="border-t border-border">
                    <td className="py-2 pr-4">{row.email}</td>
                    <td className="py-2 pr-4">
                      {`${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() || "–"}
                    </td>
                    <td className="py-2 pr-4">
                      {row.confirmed_at
                        ? new Date(row.confirmed_at).toLocaleDateString("de-DE")
                        : "offen"}
                    </td>
                    <td className="py-2">
                      {new Date(row.created_at).toLocaleDateString("de-DE")}
                    </td>
                  </tr>
                ))}
                {(signups.data ?? []).length === 0 && (
                  <tr>
                    <td className="py-3 text-muted-foreground" colSpan={4}>
                      Noch keine Anmeldungen.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </AdminCard>
      )}

      {/* Ticket LM7: Löschen nur hier auf der Detailseite, nur für Administratoren. */}
      {!isNew && role === "admin" && (
        <AdminCard
          title="Deaktivieren oder löschen"
          description={DELETE_VS_DEACTIVATE_HINT}
        >
          {deletion.isLoading && (
            <p className="mt-4 font-body text-sm text-muted-foreground">Nutzung wird geprüft …</p>
          )}
          {deletion.error && <AdminErrorNotice message={(deletion.error as Error).message} />}
          {deletion.data && !deletion.data.deletable && (
            <p className="mt-4 rounded-sm border border-border bg-muted/40 px-3 py-2 font-body text-sm">
              {deletion.data.blockReason}
            </p>
          )}
          {deletion.data?.deletable && (
            <div className="mt-4 space-y-3">
              <p className="font-body text-sm text-muted-foreground">
                Dieses Freebie hat keine Anmeldungen und ist weder an Kunden noch an Kontakten als
                Herkunft vermerkt. Es kann vollständig gelöscht werden – die hinterlegte Datei
                verschwindet mit.
              </p>
              <div className="max-w-md">
                <label className={labelClass} htmlFor="lm-delete-confirm">
                  Zum Löschen den Titel eintragen: <strong>{deletion.data.title}</strong>
                </label>
                <input
                  id="lm-delete-confirm"
                  className={inputClass}
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                />
              </div>
              <button
                type="button"
                onClick={onDelete}
                disabled={deleting || deleteConfirm.trim() === ""}
                className="inline-flex items-center gap-2 rounded-sm border border-destructive px-4 py-2 font-display text-sm font-extrabold text-destructive disabled:opacity-60"
              >
                {deleting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Lead-Magnet löschen
              </button>
            </div>
          )}
        </AdminCard>
      )}
    </div>
  );
}

